import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable, tap, map, forkJoin} from "rxjs";
import {LoaderService} from "./loader.service";
import {
  ActionResult,
  ActionResultList, ApplicationFormSettings, CompetitorCount,
  EventItem, EventStatus, FetchEventByIdRequest, FindEventsRequest,
  Lookups, Race, RaceSettings, RaceStatus
} from "@model";
import {EnvironmentService} from "./environment.service";
import {RaceSettingsService} from "./race-settings.service";
import {ApplicationFormSettingsService} from "./application-form-settings.serivce";

@Injectable({ providedIn:'root' })
export class EventService {
  private static _currentEvent: BehaviorSubject<EventItem | undefined> = new BehaviorSubject<EventItem | undefined>(undefined);

  get currentEvent(): BehaviorSubject<EventItem | undefined> {
    return EventService._currentEvent;
  }

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService:EnvironmentService,
              private raceSettingsService:RaceSettingsService, private applicationFormSettingsService: ApplicationFormSettingsService) {
  }

  setCurrentEvent(eventItem: EventItem | undefined) {
    this.currentEvent.next(eventItem);
  }

  getFilteredEventStatuses(eventId?: number): Observable<Lookups[] | []> {
    return this.http.post<ActionResult<Lookups[]>>(this.environmentService.getApiUrl() + "/api/public/lookups/statusesfiltered", eventId).pipe(
      map((value: ActionResult<Lookups[]>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  fetchEventToCopy(eventId: FetchEventByIdRequest, hideLoading?: boolean): Observable<EventItem | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + "/api/events/geteventbyid", eventId)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            let eventItem = value.result;
            eventItem.status = EventStatus.INACTIVE;
            eventItem.name = undefined;
            eventItem.nameEng = undefined;
            eventItem.nameIta = undefined;
            eventItem.nameSlo = undefined;
            eventItem.start = new Date();
            eventItem.end = new Date();
            eventItem.racesList?.forEach((race: Race) => {
              race.status = RaceStatus.INACTIVE
              race.raceStart = undefined;
              race.raceEnd = undefined;
              race.registrationEnd = undefined;
              race.registrationStart = undefined;
              race.competitorCount = new CompetitorCount();
              race.competitorCount.total = 0;
            });
            return eventItem;
          } else {
            return null;
          }
        })
      );
  }

  findEventListByName(searchFilter: FindEventsRequest): Observable<EventItem[]> {
    return this.http.post<ActionResultList<EventItem[], number>>(this.environmentService.getApiUrl() + "/api/public/events/active/byName", searchFilter).pipe(
        map(res => res?.result ?? [])
    );
  }



  fetchAdminEvents(eventFilter: FindEventsRequest, measurements:boolean = false): Observable<EventItem[]> {
    return this.http.post<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + (measurements ? "/api/events/measurementsevents" : "/api/events/organiserevents"), eventFilter).pipe(
      map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  updateEvent(eventItem: EventItem, onSuccess: () => void, onError: () => void): Observable<EventItem | null> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + (eventItem.id ? "/api/events/update" : "/api/events/add"), eventItem)
      .pipe(
        tap(() => {
          this.loaderService.dismissLoading();
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            onSuccess();
            return value.result;
          } else {
            onError();
            return null;
          }
        })
      );
  }

  deleteEvent(event: EventItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/events/delete", event.id)
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          this.loaderService.dismissLoading();
          onSuccess();
        } else {
          this.loaderService.dismissLoading();
          onError();
        }
      });
  }

  publishEvent(event: EventItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/events/publish", event.id)
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          this.loaderService.dismissLoading();
          onSuccess();
        } else {
          this.loaderService.dismissLoading();
          onError();
        }
      });
  }

  copyEvent(eventItem: EventItem, onSuccess: () => void, onError: () => void): Observable<EventItem | null> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + "/api/copy/event", eventItem)
      .pipe(
        tap(() => {
          this.loaderService.dismissLoading();
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            onSuccess();
            return value.result;
          } else {
            onError();
            return null;
          }
        })
      );
  }

  private findEventByIdProtectedInner(eventId: FetchEventByIdRequest, measurement:boolean = false): Observable<EventItem | null> {
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + (measurement ? "/api/measurements/geteventbyid":"/api/events/geteventbyid"), eventId)
      .pipe(
        map((value: ActionResult<EventItem>) => {
          return value?.result || null;
        })
      );
  }

  findEventByIdProtected(eventId: FetchEventByIdRequest, loading:boolean = false, measurement:boolean = false): Observable<EventItem | null> {
    if (loading) this.loaderService.startLoading();
    return forkJoin(
      {
        event:this.findEventByIdProtectedInner(eventId, measurement),
        raceSettingsList: this.raceSettingsService.fetchRaceSettingsByEventId(eventId.eventId, true),
        applicationFormSettingsList: this.applicationFormSettingsService.fetchApplicationFormSettingsByEventId(eventId.eventId, true)
      })
      .pipe(tap(() => {
        if (loading) this.loaderService.dismissLoading()
      }),map((data:any)=>{
      return this.combineRaceData(data);
    }));
  }

  private combineRaceData(data:any): EventItem|null {
    if (data.event) {
      data.event.racesList?.forEach((race: Race) => {
        race.raceSettings = data.raceSettingsList.find((item: RaceSettings) => item.raceId === race.id);
        race.applicationFormSettings = data.applicationFormSettingsList.find((item: ApplicationFormSettings) => item.raceId === race.id);
      });
      this.currentEvent.next(data.event);
      return data.event;
    }
    this.currentEvent.next(undefined);
    return null;
  }
}

